import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { z } from "zod";
import { ensureShopFeature } from "@/app/router/shop-feature-guards";
import {
  OffsetPageQuerySchema,
  ShopStaffIndexSearchSchema,
} from "@/shared/contracts/base";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("../modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const MyShopsPage = lazy(() =>
  import("../modules/merchant/presentation/pages/MyShopsPage").then(
    (module) => ({
      default: module.MyShopsPage,
    }),
  ),
);
const ShopDashboardPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopDashboardPage").then(
    (module) => ({ default: module.ShopDashboardPage }),
  ),
);
const ShopSettingsPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopSettingsPage").then(
    (module) => ({ default: module.ShopSettingsPage }),
  ),
);
const ShopCatalogPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopCatalogPage").then(
    (module) => ({ default: module.ShopCatalogPage }),
  ),
);
const ShopNotificationsPage = lazy(() =>
  import(
    "../modules/notifications/presentation/pages/ShopNotificationsPage"
  ).then((module) => ({ default: module.ShopNotificationsPage })),
);
const ShopServicesPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopServicesPage").then(
    (module) => ({ default: module.ShopServicesPage }),
  ),
);
const ShopCatalogSkuEditPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopCatalogSkuEditPage").then(
    (module) => ({ default: module.ShopCatalogSkuEditPage }),
  ),
);
const ShopPosPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopPosPage").then(
    (module) => ({
      default: module.ShopPosPage,
    }),
  ),
);
const ShopPosSalesHistoryPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopPosSalesHistoryPage").then(
    (module) => ({ default: module.ShopPosSalesHistoryPage }),
  ),
);
const ShopReportsPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopReportsPage").then(
    (module) => ({ default: module.ShopReportsPage }),
  ),
);
const ShopReportsBranchesPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopReportsBranchesPage").then(
    (module) => ({ default: module.ShopReportsBranchesPage }),
  ),
);
const ShopReportsInventoryPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopReportsInventoryPage"
  ).then((module) => ({ default: module.ShopReportsInventoryPage })),
);
const ShopReportsWarehouseLocationsPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopReportsWarehouseLocationsPage"
  ).then((module) => ({
    default: module.ShopReportsWarehouseLocationsPage,
  })),
);
const ShopReportsModulesPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopReportsModulesPage").then(
    (module) => ({ default: module.ShopReportsModulesPage }),
  ),
);
const ShopWarehouseLocationsPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopWarehouseLocationsPage"
  ).then((module) => ({ default: module.ShopWarehouseLocationsPage })),
);
const ShopInventoryPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopInventoryPage").then(
    (module) => ({ default: module.ShopInventoryPage }),
  ),
);
const ShopInventoryReceivePage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopInventoryReceivePage"
  ).then((module) => ({ default: module.ShopInventoryReceivePage })),
);
const ShopInventoryTransferPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopInventoryTransferPage"
  ).then((module) => ({ default: module.ShopInventoryTransferPage })),
);
const ShopInventoryReserveReleasePage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopInventoryReserveReleasePage"
  ).then((module) => ({ default: module.ShopInventoryReserveReleasePage })),
);
const ShopInventoryMovementsPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopInventoryMovementsPage"
  ).then((module) => ({ default: module.ShopInventoryMovementsPage })),
);
const ShopInventorySkuDetailPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopInventorySkuDetailPage"
  ).then((module) => ({ default: module.ShopInventorySkuDetailPage })),
);
const ShopPurchaseOrdersPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopPurchaseOrdersPage").then(
    (module) => ({ default: module.ShopPurchaseOrdersPage }),
  ),
);
const ShopSuppliersPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopSuppliersPage").then(
    (module) => ({ default: module.ShopSuppliersPage }),
  ),
);
const ShopPromotionsPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopPromotionsPage").then(
    (module) => ({ default: module.ShopPromotionsPage }),
  ),
);
const ShopWorkshopPage = lazy(() =>
  import("../modules/workshop/presentation/pages/ShopWorkshopPage").then(
    (module) => ({ default: module.ShopWorkshopPage }),
  ),
);
const ShopWorkshopJobNewPage = lazy(() =>
  import("../modules/workshop/presentation/pages/ShopWorkshopJobNewPage").then(
    (module) => ({ default: module.ShopWorkshopJobNewPage }),
  ),
);
const ShopWorkshopJobDetailPage = lazy(() =>
  import(
    "../modules/workshop/presentation/pages/ShopWorkshopJobDetailPage"
  ).then((module) => ({ default: module.ShopWorkshopJobDetailPage })),
);
const ShopWorkshopQuotesPage = lazy(() =>
  import("../modules/workshop/presentation/pages/ShopWorkshopQuotesPage").then(
    (module) => ({ default: module.ShopWorkshopQuotesPage }),
  ),
);
const ShopMarketplacePage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopMarketplacePage").then(
    (module) => ({ default: module.ShopMarketplacePage }),
  ),
);
const ShopMarketplaceOrdersPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopMarketplaceOrdersPage"
  ).then((module) => ({ default: module.ShopMarketplaceOrdersPage })),
);
const ShopPromotionFormPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopPromotionFormPage").then(
    (module) => ({ default: module.ShopPromotionFormPage }),
  ),
);
const ShopPurchaseOrderNewPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopPurchaseOrderNewPage"
  ).then((module) => ({ default: module.ShopPurchaseOrderNewPage })),
);
const ShopPurchaseOrderDetailPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopPurchaseOrderDetailPage"
  ).then((module) => ({ default: module.ShopPurchaseOrderDetailPage })),
);
const ShopPurchaseOrderReceivePage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopPurchaseOrderReceivePage"
  ).then((module) => ({
    default: module.ShopPurchaseOrderReceivePage,
  })),
);
const ShopPlaceholderPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopPlaceholderPage").then(
    (module) => ({ default: module.ShopPlaceholderPage }),
  ),
);
const ShopSettlementPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopSettlementPage").then(
    (module) => ({ default: module.ShopSettlementPage }),
  ),
);
const ShopRolesPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopRolesPage").then(
    (module) => ({ default: module.ShopRolesPage }),
  ),
);
const ShopRoleFormPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopRoleFormPage").then(
    (module) => ({ default: module.ShopRoleFormPage }),
  ),
);
const ShopStaffPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopStaffPage").then(
    (module) => ({ default: module.ShopStaffPage }),
  ),
);
const ShopBranchesPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopBranchesPage").then(
    (module) => ({ default: module.ShopBranchesPage }),
  ),
);
const ShopShiftCalendarPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopShiftCalendarPage").then(
    (module) => ({ default: module.ShopShiftCalendarPage }),
  ),
);
const ShopMyShiftsPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopMyShiftsPage").then(
    (module) => ({ default: module.ShopMyShiftsPage }),
  ),
);
const ShopShiftReportPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopShiftReportPage").then(
    (module) => ({ default: module.ShopShiftReportPage }),
  ),
);
const ShopProductClaimNewPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopProductClaimNewPage").then(
    (module) => ({ default: module.ShopProductClaimNewPage }),
  ),
);
const ShopProductClaimEditPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopProductClaimEditPage"
  ).then((module) => ({ default: module.ShopProductClaimEditPage })),
);
const ShopProductClaimDetailPage = lazy(() =>
  import(
    "../modules/merchant/presentation/pages/ShopProductClaimDetailPage"
  ).then((module) => ({ default: module.ShopProductClaimDetailPage })),
);
const ShopProductClaimsPage = lazy(() =>
  import("../modules/merchant/presentation/pages/ShopProductClaimsPage").then(
    (module) => ({ default: module.ShopProductClaimsPage }),
  ),
);
const ProfilePage = lazy(() =>
  import("../modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (location.pathname === "/") {
      throw redirect({ to: "/app", replace: true });
    }
  },
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const appIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: () => (
    <LazyPage>
      <MyShopsPage />
    </LazyPage>
  ),
});

const shopsLayoutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "shops/$shopId",
  component: () => <Outlet />,
});

const shopDashboardRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "/",
  component: () => (
    <LazyPage>
      <ShopDashboardPage />
    </LazyPage>
  ),
});

const shopSettingsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "settings",
  component: () => (
    <LazyPage>
      <ShopSettingsPage />
    </LazyPage>
  ),
});

const shopCatalogRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "catalog",
  component: () => (
    <LazyPage>
      <ShopCatalogPage />
    </LazyPage>
  ),
});
const shopServicesRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "services",
  component: () => (
    <LazyPage>
      <ShopServicesPage />
    </LazyPage>
  ),
});
const shopCatalogSkuEditRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "catalog/sellable/$shopProductSkuId/edit",
  component: () => (
    <LazyPage>
      <ShopCatalogSkuEditPage />
    </LazyPage>
  ),
});
const shopInventoryReceiveRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "inventory/receive",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
  },
  validateSearch: (raw: Record<string, unknown>) => ({
    branchId:
      typeof raw.branchId === "string" && raw.branchId.length > 0
        ? raw.branchId
        : undefined,
    shopProductSkuId:
      typeof raw.shopProductSkuId === "string" &&
      raw.shopProductSkuId.length > 0
        ? raw.shopProductSkuId
        : undefined,
  }),
  component: () => (
    <LazyPage>
      <ShopInventoryReceivePage />
    </LazyPage>
  ),
});
const shopInventoryTransferRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "inventory/transfer",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
  },
  component: () => (
    <LazyPage>
      <ShopInventoryTransferPage />
    </LazyPage>
  ),
});
const shopInventoryReserveRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "inventory/reserve",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
  },
  component: () => (
    <LazyPage>
      <ShopInventoryReserveReleasePage />
    </LazyPage>
  ),
});
const shopInventoryMovementsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "inventory/movements",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
  },
  validateSearch: (raw: Record<string, unknown>) => {
    const offset = Number(raw.offset);
    const limit = Number(raw.limit);
    const recRaw = raw.recOnlyMismatch;
    const recOnlyMismatch = !(
      recRaw === false ||
      recRaw === "false" ||
      recRaw === "0"
    );
    return {
      offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
      limit: Number.isFinite(limit) && limit >= 1 ? Math.min(100, limit) : 20,
      branchId:
        typeof raw.branchId === "string" && raw.branchId.trim() !== ""
          ? raw.branchId.trim()
          : undefined,
      shopProductSkuId:
        typeof raw.shopProductSkuId === "string" &&
        raw.shopProductSkuId.trim() !== ""
          ? raw.shopProductSkuId.trim()
          : undefined,
      type:
        typeof raw.type === "string" && raw.type.trim() !== ""
          ? raw.type.trim()
          : undefined,
      createdFrom:
        typeof raw.createdFrom === "string" && raw.createdFrom.trim() !== ""
          ? raw.createdFrom.trim()
          : undefined,
      createdTo:
        typeof raw.createdTo === "string" && raw.createdTo.trim() !== ""
          ? raw.createdTo.trim()
          : undefined,
      recOnlyMismatch,
    };
  },
  component: () => (
    <LazyPage>
      <ShopInventoryMovementsPage />
    </LazyPage>
  ),
});
const shopInventorySkuDetailRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "inventory/skus/$shopProductSkuId",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
  },
  validateSearch: (raw: Record<string, unknown>) => ({
    branchId:
      typeof raw.branchId === "string" && raw.branchId.trim() !== ""
        ? raw.branchId.trim()
        : undefined,
  }),
  component: () => (
    <LazyPage>
      <ShopInventorySkuDetailPage />
    </LazyPage>
  ),
});
const shopWarehouseLocationsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "inventory/locations",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
    ensureShopFeature(params.shopId, "warehouse_locations");
  },
  validateSearch: (raw: Record<string, unknown>) => ({
    tab:
      raw.tab === "layout" || raw.tab === "list"
        ? (raw.tab as "layout" | "list")
        : undefined,
    branchId:
      typeof raw.branchId === "string" && raw.branchId.trim() !== ""
        ? raw.branchId.trim()
        : undefined,
    layoutId:
      typeof raw.layoutId === "string" && raw.layoutId.trim() !== ""
        ? raw.layoutId.trim()
        : undefined,
    focusLocationId:
      typeof raw.focusLocationId === "string" &&
      raw.focusLocationId.trim() !== ""
        ? raw.focusLocationId.trim()
        : undefined,
  }),
  component: () => (
    <LazyPage>
      <ShopWarehouseLocationsPage />
    </LazyPage>
  ),
});
const shopInventoryRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "inventory",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
  },
  component: () => (
    <LazyPage>
      <ShopInventoryPage />
    </LazyPage>
  ),
});
const shopPurchaseOrdersNewRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "purchase-orders/new",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "procurement");
  },
  component: () => (
    <LazyPage>
      <ShopPurchaseOrderNewPage />
    </LazyPage>
  ),
});
const shopPurchaseOrderReceiveRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "purchase-orders/$purchaseOrderId/receive",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "procurement");
  },
  validateSearch: (raw: Record<string, unknown>) => ({
    shopProductSkuId:
      typeof raw.shopProductSkuId === "string" &&
      raw.shopProductSkuId.trim() !== ""
        ? raw.shopProductSkuId.trim()
        : undefined,
  }),
  component: () => (
    <LazyPage>
      <ShopPurchaseOrderReceivePage />
    </LazyPage>
  ),
});
const shopPurchaseOrderDetailRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "purchase-orders/$purchaseOrderId",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "procurement");
  },
  component: () => (
    <LazyPage>
      <ShopPurchaseOrderDetailPage />
    </LazyPage>
  ),
});
const shopPurchaseOrdersRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "purchase-orders",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "procurement");
  },
  validateSearch: (raw: Record<string, unknown>) => {
    const offset = Number(raw.offset);
    const limit = Number(raw.limit);
    const status =
      typeof raw.status === "string" && raw.status.trim() !== ""
        ? raw.status.trim()
        : undefined;
    const branchId =
      typeof raw.branchId === "string" && raw.branchId.trim() !== ""
        ? raw.branchId.trim()
        : undefined;
    const sortFields = new Set([
      "createdAt",
      "poNumber",
      "status",
      "currency",
      "branchName",
      "lineCount",
    ]);
    const sortByRaw =
      typeof raw.sortBy === "string" && sortFields.has(raw.sortBy.trim())
        ? raw.sortBy.trim()
        : undefined;
    const sortOrderRaw =
      raw.sortOrder === "asc" || raw.sortOrder === "desc"
        ? raw.sortOrder
        : undefined;
    const supplierId =
      typeof raw.supplierId === "string" && raw.supplierId.trim() !== ""
        ? raw.supplierId.trim()
        : undefined;
    const dueRaw =
      typeof raw.due === "string" && raw.due.trim() !== ""
        ? raw.due.trim()
        : undefined;
    const due = dueRaw === "soon" || dueRaw === "overdue" ? dueRaw : undefined;
    return {
      offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
      limit: Number.isFinite(limit) && limit >= 1 ? Math.min(100, limit) : 20,
      status,
      branchId,
      supplierId,
      due,
      sortBy: sortByRaw as
        | "createdAt"
        | "poNumber"
        | "status"
        | "currency"
        | "branchName"
        | "lineCount"
        | undefined,
      sortOrder: sortOrderRaw,
    };
  },
  component: () => (
    <LazyPage>
      <ShopPurchaseOrdersPage />
    </LazyPage>
  ),
});
const shopSuppliersRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "suppliers",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "procurement");
  },
  validateSearch: (raw: Record<string, unknown>) => {
    const offset = Number(raw.offset);
    const limit = Number(raw.limit);
    const search =
      typeof raw.search === "string" && raw.search.trim() !== ""
        ? raw.search.trim().slice(0, 200)
        : undefined;
    const af = raw.activeFilter;
    const activeFilter =
      af === "all" || af === "active" || af === "inactive" ? af : "all";
    return {
      offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
      limit: Number.isFinite(limit) && limit >= 1 ? Math.min(100, limit) : 20,
      search,
      activeFilter,
    };
  },
  component: () => (
    <LazyPage>
      <ShopSuppliersPage />
    </LazyPage>
  ),
});
const shopPromotionsNewRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "promotions/new",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "promotions");
  },
  component: () => (
    <LazyPage>
      <ShopPromotionFormPage />
    </LazyPage>
  ),
});
const shopPromotionsEditRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "promotions/$promotionId/edit",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "promotions");
  },
  component: () => (
    <LazyPage>
      <ShopPromotionFormPage />
    </LazyPage>
  ),
});
const shopMarketplaceRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "marketplace",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "marketplace_sync");
  },
  validateSearch: (raw: Record<string, unknown>) => {
    const offset = Number(raw.offset);
    const limit = Number(raw.limit);
    const q =
      typeof raw.q === "string" && raw.q.trim() !== ""
        ? raw.q.trim().slice(0, 200)
        : undefined;
    return {
      offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
      limit: Number.isFinite(limit) && limit >= 1 ? Math.min(100, limit) : 20,
      q,
    };
  },
  component: () => (
    <LazyPage>
      <ShopMarketplacePage />
    </LazyPage>
  ),
});
const shopMarketplaceOrdersRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "marketplace/orders",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "marketplace_sync");
  },
  component: () => (
    <LazyPage>
      <ShopMarketplaceOrdersPage />
    </LazyPage>
  ),
});
const shopNotificationsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "notifications",
  validateSearch: (raw: Record<string, unknown>) => ({
    limit: Number(raw.limit) || 20,
    offset: Number(raw.offset) || 0,
    type: typeof raw.type === "string" && raw.type ? raw.type : undefined,
    unreadOnly: raw.unreadOnly === true || raw.unreadOnly === "true",
  }),
  component: () => (
    <LazyPage>
      <ShopNotificationsPage />
    </LazyPage>
  ),
});
const shopPromotionsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "promotions",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "promotions");
  },
  validateSearch: (raw: Record<string, unknown>) => {
    const offset = Number(raw.offset);
    const limit = Number(raw.limit);
    const st = raw.status;
    const status =
      st === "all" || st === "draft" || st === "active" || st === "paused"
        ? st
        : "all";
    const search =
      typeof raw.search === "string" && raw.search.trim() !== ""
        ? raw.search.trim().slice(0, 200)
        : undefined;
    return {
      offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
      limit: Number.isFinite(limit) && limit >= 1 ? Math.min(100, limit) : 20,
      status,
      search,
    };
  },
  component: () => (
    <LazyPage>
      <ShopPromotionsPage />
    </LazyPage>
  ),
});
const shopWorkshopRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "workshop",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "workshop");
  },
  component: () => (
    <LazyPage>
      <ShopWorkshopPage />
    </LazyPage>
  ),
});
const shopWorkshopQuotesRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "workshop/quotes",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "workshop");
  },
  component: () => (
    <LazyPage>
      <ShopWorkshopQuotesPage />
    </LazyPage>
  ),
});
const shopWorkshopJobNewRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "workshop/jobs/new",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "workshop");
  },
  component: () => (
    <LazyPage>
      <ShopWorkshopJobNewPage />
    </LazyPage>
  ),
});
const shopWorkshopJobDetailRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "workshop/jobs/$jobId",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "workshop");
  },
  component: () => (
    <LazyPage>
      <ShopWorkshopJobDetailPage />
    </LazyPage>
  ),
});
const shopPosRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "pos",
  validateSearch: (raw: Record<string, unknown>) => {
    const focus =
      typeof raw.focus === "string" && raw.focus.trim() === "scan"
        ? ("scan" as const)
        : undefined;
    return { focus };
  },
  component: () => (
    <LazyPage>
      <ShopPosPage />
    </LazyPage>
  ),
});
const shopPosSalesRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "pos/sales",
  validateSearch: (raw: Record<string, unknown>) => {
    const offset = Number(raw.offset);
    const limit = Number(raw.limit);
    const status =
      typeof raw.status === "string" && raw.status.trim() !== ""
        ? raw.status.trim()
        : undefined;
    const str = (k: string) => {
      const v = raw[k];
      return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
    };
    return {
      offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
      limit: Number.isFinite(limit) && limit >= 1 ? Math.min(100, limit) : 20,
      status,
      createdFrom: str("createdFrom"),
      createdTo: str("createdTo"),
      saleNumber: str("saleNumber"),
      customerUserId: str("customerUserId"),
      customerSearch: str("customerSearch"),
    };
  },
  component: () => (
    <LazyPage>
      <ShopPosSalesHistoryPage />
    </LazyPage>
  ),
});

const shopReportsBranchesRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "reports/branches",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "multi_branch");
  },
  component: () => (
    <LazyPage>
      <ShopReportsBranchesPage />
    </LazyPage>
  ),
});

const shopReportsInventoryRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "reports/inventory",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
  },
  component: () => (
    <LazyPage>
      <ShopReportsInventoryPage />
    </LazyPage>
  ),
});

const shopReportsWarehouseLocationsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "reports/warehouse-locations",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "inventory");
    ensureShopFeature(params.shopId, "warehouse_locations");
  },
  component: () => (
    <LazyPage>
      <ShopReportsWarehouseLocationsPage />
    </LazyPage>
  ),
});

const shopReportsModulesRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "reports/modules",
  component: () => (
    <LazyPage>
      <ShopReportsModulesPage />
    </LazyPage>
  ),
});

const shopReportsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "reports",
  component: () => (
    <LazyPage>
      <ShopReportsPage />
    </LazyPage>
  ),
});

const shopSettlementRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "settlement",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "settlement");
  },
  component: () => (
    <LazyPage>
      <ShopSettlementPage />
    </LazyPage>
  ),
});
const shopBranchesRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "branches",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "multi_branch");
  },
  component: () => (
    <LazyPage>
      <ShopBranchesPage />
    </LazyPage>
  ),
});

const shopShiftsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "shifts",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "shifts");
    throw redirect({
      to: "/app/shops/$shopId/shifts/calendar",
      params: { shopId: params.shopId },
    });
  },
});

const shopShiftsCalendarRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "shifts/calendar",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "shifts");
  },
  component: () => (
    <LazyPage>
      <ShopShiftCalendarPage />
    </LazyPage>
  ),
});

const shopShiftsMyRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "shifts/me",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "shifts");
  },
  component: () => (
    <LazyPage>
      <ShopMyShiftsPage />
    </LazyPage>
  ),
});

const shopShiftsReportRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "shifts/reports",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "shifts");
  },
  component: () => (
    <LazyPage>
      <ShopShiftReportPage />
    </LazyPage>
  ),
});

const shopProductClaimNewRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "product-claims/new",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "claims");
  },
  validateSearch: (raw: Record<string, unknown>) => {
    const branchId =
      typeof raw.branchId === "string" && raw.branchId.trim() !== ""
        ? raw.branchId.trim()
        : undefined;
    const supplierIdRaw =
      typeof raw.supplierId === "string" ? raw.supplierId.trim() : "";
    const supplierId =
      supplierIdRaw.length > 0 &&
      z.string().uuid().safeParse(supplierIdRaw).success
        ? supplierIdRaw
        : undefined;
    return { branchId, supplierId };
  },
  component: () => (
    <LazyPage>
      <ShopProductClaimNewPage />
    </LazyPage>
  ),
});

const shopProductClaimEditRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "product-claims/$claimId/edit",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "claims");
  },
  component: () => (
    <LazyPage>
      <ShopProductClaimEditPage />
    </LazyPage>
  ),
});

const shopProductClaimDetailRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "product-claims/$claimId",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "claims");
  },
  component: () => (
    <LazyPage>
      <ShopProductClaimDetailPage />
    </LazyPage>
  ),
});

const shopProductClaimsRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "product-claims",
  beforeLoad: ({ params }) => {
    ensureShopFeature(params.shopId, "claims");
  },
  validateSearch: (raw: Record<string, unknown>) => {
    const offset = Number(raw.offset);
    const limit = Number(raw.limit);
    const branchId =
      typeof raw.branchId === "string" && raw.branchId.trim() !== ""
        ? raw.branchId.trim()
        : undefined;
    const status =
      typeof raw.status === "string" && raw.status.trim() !== ""
        ? raw.status.trim()
        : undefined;
    const supplierIdRaw =
      typeof raw.supplierId === "string" ? raw.supplierId.trim() : "";
    const supplierId =
      supplierIdRaw.length > 0 &&
      z.string().uuid().safeParse(supplierIdRaw).success
        ? supplierIdRaw
        : undefined;
    const wl = typeof raw.warrantyLink === "string" ? raw.warrantyLink : "";
    const warrantyLink = wl === "linked" || wl === "none" ? wl : undefined;
    return {
      offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
      limit: Number.isFinite(limit) && limit >= 1 ? Math.min(100, limit) : 20,
      branchId,
      status,
      supplierId,
      warrantyLink,
    };
  },
  component: () => (
    <LazyPage>
      <ShopProductClaimsPage />
    </LazyPage>
  ),
});

const staffLayoutRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "staff",
  component: () => <Outlet />,
});

const shopStaffIndexRoute = createRoute({
  getParentRoute: () => staffLayoutRoute,
  path: "/",
  validateSearch: (raw: Record<string, unknown>) => {
    const parsed = ShopStaffIndexSearchSchema.safeParse(raw);
    if (parsed.success) return parsed.data;
    const base = OffsetPageQuerySchema.safeParse(raw);
    let assignBranchId: string | undefined;
    if (typeof raw.assignBranchId === "string") {
      const t = raw.assignBranchId.trim();
      if (t.length > 0 && z.string().uuid().safeParse(t).success)
        assignBranchId = t;
    }
    return {
      ...(base.success ? base.data : { limit: 20, offset: 0 }),
      assignBranchId,
    };
  },
  component: () => (
    <LazyPage>
      <ShopStaffPage />
    </LazyPage>
  ),
});

const rolesLayoutRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "roles",
  component: () => <Outlet />,
});

const shopRolesIndexRoute = createRoute({
  getParentRoute: () => rolesLayoutRoute,
  path: "/",
  component: () => (
    <LazyPage>
      <ShopRolesPage />
    </LazyPage>
  ),
});

const shopRoleNewRoute = createRoute({
  getParentRoute: () => rolesLayoutRoute,
  path: "new",
  component: () => (
    <LazyPage>
      <ShopRoleFormPage />
    </LazyPage>
  ),
});

const shopRoleEditRoute = createRoute({
  getParentRoute: () => rolesLayoutRoute,
  path: "$roleId/edit",
  component: () => (
    <LazyPage>
      <ShopRoleFormPage />
    </LazyPage>
  ),
});

const shopProfileRoute = createRoute({
  getParentRoute: () => shopsLayoutRoute,
  path: "profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    appIndexRoute,
    shopsLayoutRoute.addChildren([
      shopDashboardRoute,
      shopSettingsRoute,
      shopCatalogRoute,
      shopServicesRoute,
      shopCatalogSkuEditRoute,
      shopInventoryReceiveRoute,
      shopInventoryTransferRoute,
      shopInventoryReserveRoute,
      shopInventoryMovementsRoute,
      shopInventorySkuDetailRoute,
      shopWarehouseLocationsRoute,
      shopPurchaseOrdersNewRoute,
      shopPurchaseOrderReceiveRoute,
      shopPurchaseOrderDetailRoute,
      shopPurchaseOrdersRoute,
      shopSuppliersRoute,
      shopPromotionsNewRoute,
      shopPromotionsEditRoute,
      shopPromotionsRoute,
      shopWorkshopRoute,
      shopWorkshopQuotesRoute,
      shopWorkshopJobNewRoute,
      shopWorkshopJobDetailRoute,
      shopMarketplaceRoute,
      shopMarketplaceOrdersRoute,
      shopNotificationsRoute,
      shopInventoryRoute,
      shopPosRoute,
      shopPosSalesRoute,
      shopReportsBranchesRoute,
      shopReportsInventoryRoute,
      shopReportsWarehouseLocationsRoute,
      shopReportsModulesRoute,
      shopReportsRoute,
      shopSettlementRoute,
      shopBranchesRoute,
      shopShiftsRoute,
      shopShiftsCalendarRoute,
      shopShiftsMyRoute,
      shopShiftsReportRoute,
      shopProductClaimNewRoute,
      shopProductClaimEditRoute,
      shopProductClaimDetailRoute,
      shopProductClaimsRoute,
      staffLayoutRoute.addChildren([shopStaffIndexRoute]),
      rolesLayoutRoute.addChildren([
        shopRolesIndexRoute,
        shopRoleNewRoute,
        shopRoleEditRoute,
      ]),
      shopProfileRoute,
    ]),
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
